import { DarkThemeToggle } from "@/components/DarkThemeToggle/DarkThemeToggle";
import { PushNotificationSettings } from "@/components/PushNotificationSettings";
import { TrustedComputerToggle } from "@/components/TrustedComputerToggle";
import { useSubscriptionAccess } from "@/hooks/useSubscriptionAccess";
import { Button, Separator as Divider, Typography } from "@iluvatar/ui-kit";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { extend } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useCallback, useState } from "react";

extend(relativeTime);

import {
  IFieldGroupBuilt,
  IFieldTypes,
  UserEntity,
} from "@iluvatar/global/src/typings";
import { DeepPartial } from "@iluvatar/global/src/typings/ts-utils";
import { flattenObject, unFlattenObject } from "@iluvatar/global/src/utilities";

import { FormHelpers } from "@/FormLib";
import { FlexSpacer } from "@/components/FlexSpacer";
import { Form, FormState } from "@/components/Form";
import { RenderFormDefinition } from "@/components/Form/FormDefinitionRender";
import { formErrorHandler } from "@/components/Form/helpers";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import DefaultLayout from "@/layout/default-page";

const UserAccountSettings: React.FC = () => {
  const params: { userId: string } = useParams({ strict: false });
  const [accountForm, setAccount] = useState<FormState>({
    hasChanges: false,
  });

  const { user, api } = useCurrentUser();
  const hasSubscriptionAccess = useSubscriptionAccess(user?.isSubscribed);

  const onUserUpdate = useCallback(
    async function innerUserHandler(
      userData: DeepPartial<UserEntity>,
      helpers: FormHelpers<DeepPartial<UserEntity>>,
    ) {
      try {
        await api.updateUser(params.userId, userData);
        await helpers.resetForm();
      } catch (error) {
        formErrorHandler(error, helpers);
      }
    },
    [api, params],
  );

  return (
    <DefaultLayout
      className={
        "profile-container ml-auto mr-auto [max-width:900px] flex flex-col [background:linear-gradient(180deg,_transparent_0%,_transparent_50%,_var(--color-background-default)_50%,_var(--color-background-default))] [@media(min-width:_900px)]:[--page-content-padding:42px] [@media(min-width:_900px)]:[padding-left:42px] [@media(min-width:_900px)]:[padding-right:42px] [&.profile-container--input]:[max-width:900px] [&.profile-container--input]:w-full [@media(max-width:_400px)]:[&.main-container]:[--page-content-padding:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-left:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-right:8px] flex start-start pb-12"
      }
    >
      <section
        aria-busy={!user}
        aria-labelledby="account-settings-title"
        className="w-full"
      >
        <Typography
          id="account-settings-title"
          variant="h2"
          className="flex mt-12 mb-8"
        >
          Account Settings
          <FlexSpacer />
          {accountForm.hasChanges && (
            <Button variant="outline" type="submit" form="account-settings">
              Save
            </Button>
          )}
        </Typography>
        {user && (
          <Form<DeepPartial<UserEntity>>
            definitionRenderer={RenderFormDefinition}
            id="account-settings"
            enableReinitialize
            initialValues={pickOutFormData(user, accountFormDefinition)}
            formDefinition={{
              ...accountFormDefinition,
              fields: accountFormDefinition.fields?.map((field) =>
                field.name === "incognito"
                  ? { ...field, disabled: !hasSubscriptionAccess }
                  : field,
              ),
            }}
            setFormMetadata={setAccount}
            onSubmit={onUserUpdate}
          />
        )}
        {!user && (
          <div
            role="status"
            aria-label="Loading account settings"
            aria-live="polite"
            className="h-[440px] animate-pulse space-y-6"
          >
            <span className="sr-only">Loading account settings</span>
            {Array.from({ length: 5 }, (_, index) => (
              <div
                key={index}
                className="h-12 w-full rounded-lg bg-[var(--color-primary--active)]"
              />
            ))}
          </div>
        )}
      </section>
      <section aria-labelledby="system-settings-title" className="w-full">
        <Divider className="my-8" />
        <Typography id="system-settings-title" variant="h2">
          System Settings
        </Typography>
        <Typography variant="overline" className="mb-8">
          These settings apply to this browser.
        </Typography>
        <TrustedComputerToggle inlineDescription />
        <div className="flex items-center gap-3 mt-6">
          <Typography>Light/Dark theme</Typography>
          <FlexSpacer />
          <DarkThemeToggle />
        </div>
        <PushNotificationSettings />
      </section>
      {import.meta.env.REACT_APP_RELEASE && (
        <Typography
          as="footer"
          variant="caption"
          className="mt-8 w-full text-center opacity-50"
        >
          Build {import.meta.env.REACT_APP_RELEASE}
        </Typography>
      )}
    </DefaultLayout>
  );
};

const accountFormDefinition: IFieldGroupBuilt = {
  name: "",
  label: "",
  fields: [
    {
      name: "active",
      label: "Active",
      description: "Turn off to pauses your account.",
      span: 1,
      fieldType: IFieldTypes.toggle,
    },
    {
      name: "incognito",
      label: "Incognito",
      description:
        "Go incognito across all you your profiles and communities. You will be able to see and like profiles, but not be seen by others.",
      span: 1,
      disabled: true,
      fieldType: IFieldTypes.toggle,
    },
    {
      name: "email",
      label: "Email",
      span: 3,
      fieldType: IFieldTypes.emailField,
    },
    {
      name: "phoneNumber",
      label: "Phone Number",
      span: 3,
      disabled: true,
      fieldType: IFieldTypes.phoneField,
    },
    {
      name: "about.displayName",
      label: "Default Display Name",
      span: 2,
      fieldType: IFieldTypes.textField,
    },
    {
      name: "about.birthDate",
      label: "Birthday",
      span: 1,
      fieldType: IFieldTypes.dateField,
      outputNumber: true,
    },
  ],
};

function pickOutFormData(
  data: UserEntity,
  form: IFieldGroupBuilt,
): DeepPartial<UserEntity> {
  const flat = flattenObject(data, undefined, { preserveArray: true });
  return unFlattenObject(
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    Object.keys(flat).reduce<any>((acc, key) => {
      if (form.fields!.some((f) => f.name === key)) {
        acc[key] = flat[key];
      }
      return acc;
    }, {}),
  ) as DeepPartial<UserEntity>;
}

export const Route = createFileRoute("/user/$userId/account-settings")({
  component: UserAccountSettings,
});
